// FeesPanel — school-wide pending fee overview for the admin: a total
// pending amount, a class-wise breakdown, and every student with a pending
// month. Clicking a student jumps straight into their profile's Fees tab,
// where fees are actually marked paid and receipts printed — this page is
// the entry point into that, not a dead-end summary.
import { useEffect, useMemo, useState } from 'react';
import { getFeesSummary } from '../api/client';
import { Card, CardHeader, KpiTile } from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { inr } from '../ui/format';

const STATUS = {
  paid:           { label: 'Paid',        tone: 'success' },
  pending:        { label: 'Pending',     tone: 'critical' },
  not_configured: { label: 'Not set up',  tone: 'neutral' },
};

const inputCls = 'border border-line rounded-s px-2 py-1.5 text-xs bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';

export default function FeesPanel({ onOpenStudent }) {
  const [summary, setSummary] = useState(null);
  const [msg,     setMsg]     = useState('');
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setMsg('');
    try {
      setSummary(await getFeesSummary());
    } catch (err) {
      setSummary(null);
      setMsg('Failed to load fees summary: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }

  const classOptions = useMemo(() =>
    (summary?.classes || []).map(c => [String(c.class_id ?? 'unassigned'), c.class_name]),
    [summary]);

  // No class picked: just the defaulters, same as before. Pick a class and
  // you get that class's whole roster (paid students included) so you can
  // see who's already clear, not only who owes.
  const filteredStudents = useMemo(() => {
    if (!summary) return [];
    if (!classFilter) return summary.students.filter(s => s.status === 'pending');
    return summary.students.filter(s => String(s.class_id ?? 'unassigned') === classFilter);
  }, [summary, classFilter]);

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Fees</h2>

      {msg && <p className="text-critical text-sm font-bold mb-3">{msg}</p>}
      {loading && <p className="text-ink-soft text-sm">Loading…</p>}

      {!loading && summary && (
        <>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5 mb-5">
            <KpiTile label="Total to Collect Every Month" value={`₹${summary.expected_monthly_total}`} />
            <KpiTile label="Collected This Month" value={`₹${summary.collected_this_month}`} deltaTone="success" />
            <KpiTile label="Total Pending (all months, all classes)" value={`₹${summary.total_pending_amount}`} deltaTone="critical" />
            <KpiTile label="Students with Pending Fees" value={summary.total_students_pending} />
          </div>

          <Card className="mb-6">
            <CardHeader title={`${classFilter ? 'Students' : 'Students with pending fees'} (${filteredStudents.length})`} action={
              <select value={classFilter} onChange={e => setClassFilter(e.target.value)} aria-label="Filter by class" className={inputCls}>
                <option value="">All classes (pending only)</option>
                {classOptions.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
              </select>
            } />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className="px-4 py-2.5">Student</th>
                    <th className="px-4 py-2.5">Class</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Months pending</th>
                    <th className="px-4 py-2.5">Amount due</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((s) => (
                    <tr key={s.student_id} className="border-t border-line-soft hover:bg-transit-tint/40">
                      <td className="px-4 py-3">
                        <button onClick={() => onOpenStudent(s.student_id, 'fees')} className="font-semibold text-transit-deep hover:underline">{s.name}</button>
                      </td>
                      <td className="px-4 py-3">{s.class_name}</td>
                      <td className="px-4 py-3"><Badge tone={STATUS[s.status].tone}>{STATUS[s.status].label}</Badge></td>
                      <td className="px-4 py-3">{s.pending_months || '—'}</td>
                      <td className="px-4 py-3 font-bold tabular-nums">{s.status === 'not_configured' ? '—' : inr(s.amount_due)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => onOpenStudent(s.student_id, 'fees')}>
                          {s.status === 'pending' ? 'Collect / Receipt' : 'Open ledger'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!filteredStudents.length && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">{classFilter ? 'No students in this class' : 'No pending fees'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="mb-6">
            <CardHeader title="By Class" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className="px-4 py-2.5">Class</th>
                    <th className="px-4 py-2.5">Students Pending</th>
                    <th className="px-4 py-2.5">Total Students</th>
                    <th className="px-4 py-2.5">Pending Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.classes.map((c) => (
                    <tr key={c.class_id ?? 'unassigned'} className="border-t border-line-soft hover:bg-transit-tint/40">
                      <td className="px-4 py-3">{c.class_name}</td>
                      <td className={`px-4 py-3 font-bold ${c.students_pending ? 'text-critical' : 'text-success'}`}>
                        {c.students_pending}
                      </td>
                      <td className="px-4 py-3">{c.total_students}</td>
                      <td className="px-4 py-3 font-bold">₹{c.pending_amount}</td>
                    </tr>
                  ))}
                  {!summary.classes.length && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-ink-soft">No students found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader title="Monthly Collections" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className="px-4 py-2.5">Month</th>
                    <th className="px-4 py-2.5">Amount Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.monthly_collections.map((m) => (
                    <tr key={`${m.year}-${m.month}`} className="border-t border-line-soft hover:bg-transit-tint/40">
                      <td className="px-4 py-3">{m.label}</td>
                      <td className="px-4 py-3 font-bold">₹{m.collected_amount}</td>
                    </tr>
                  ))}
                  {!summary.monthly_collections.length && (
                    <tr><td colSpan={2} className="px-4 py-8 text-center text-ink-soft">No payments recorded yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
